/**
 * THE MECHANICRANK CERTIFICATION TRACKS.
 *
 * Anthony: "Did you load certification track inside the crm like I had my
 * page in there before?"
 *
 * No — the certifications screen I built took free text, so a technician's
 * ASE ticket and a MechanicRank track looked like the same kind of thing
 * and neither linked anywhere. These are the real thirteen, with the codes
 * and the slugs the site already uses, so a card can say what somebody has
 * passed and link straight to the study material for what they have not.
 *
 * NOT A COPY OF THE EXAM. Sixty questions per track live on MechanicRank
 * and belong there — a shop CRM holding a question bank would be two
 * copies of the same thing drifting apart. This is the catalogue: what
 * exists, what it is called, and where to go and take it.
 *
 * The body is "MechanicRank" so these sit alongside ASE and a
 * manufacturer's course in the same list, which is how a shop actually
 * thinks about what its people hold.
 */

export const CERT_BODY = "MechanicRank";

export type Track = {
  /** The exam code, and what a certification's `name` is stored as. */
  code: string;
  title: string;
  /** Where the study material lives. */
  slug: string;
  what: string;
};

export const TRACKS: Track[] = [
  { code: "BRK", title: "Brakes & ABS", slug: "brakes-abs",
    what: "Hydraulics, friction, ABS faults and bleeding" },
  { code: "DIA", title: "Diagnostics & Drivability", slug: "diagnostics-drivability",
    what: "OBD-II, live data, and finding the cause rather than the code" },
  { code: "DSL", title: "Diesel & Heavy Duty", slug: "diesel-heavy-duty",
    what: "Injection, aftertreatment and heavy-duty electrics" },
  { code: "ELE", title: "Electrical & Electronics", slug: "electrical-electronics",
    what: "Circuits, parasitic draw, networks and wiring faults" },
  { code: "ENG", title: "Engine Repair & Rebuild", slug: "engine-repair-rebuild",
    what: "Mechanical diagnosis, timing, and rebuild practice" },
  { code: "EVH", title: "EV & Hybrid", slug: "ev-hybrid",
    what: "High voltage safety, packs, and hybrid drivetrains" },
  { code: "HVA", title: "HVAC & Climate Control", slug: "hvac-ac",
    what: "Refrigerant, compressors and cabin comfort faults" },
  { code: "MOB", title: "Mobile & Roadside Service", slug: "mobile-roadside",
    what: "Working out of a van, on a driveway, with what you carry" },
  { code: "PMI", title: "Preventive Maintenance & Inspection", slug: "preventive-maintenance",
    what: "Service intervals and the inspection that finds the next job" },
  { code: "PPI", title: "Pre-Purchase Inspection", slug: "pre-purchase-inspection",
    what: "What to check, what to report, and what not to promise" },
  { code: "PRF", title: "Performance & Forced Induction", slug: "performance-forced-induction",
    what: "Boost, tuning and what breaks when power goes up" },
  { code: "SUS", title: "Suspension, Steering & Alignment", slug: "suspension-steering",
    what: "Geometry, wear diagnosis and alignment" },
  { code: "TRN", title: "Transmission & Drivetrain", slug: "transmission-drivetrain",
    what: "Automatics, manuals, transfer cases and differentials" },
];

const SITE = "https://mechanicrank.com";

export const studyUrl = (t: Track): string => `${SITE}/learn/${t.slug}/`;
export const examUrl = (): string => `${SITE}/certification/`;

export const trackByCode = (code: string): Track | undefined =>
  TRACKS.find((t) => t.code.toUpperCase() === String(code ?? "").toUpperCase());

/**
 * A certification row IS a MechanicRank track when the awarding body says
 * so and the name is one of the codes. Everything else — ASE, a
 * manufacturer's course, an EPA ticket — stays free text, which is right:
 * a shop holds all sorts and only these thirteen have a page to link to.
 */
export const trackFor = (body: string, name: string): Track | undefined =>
  String(body ?? "").trim().toLowerCase() === CERT_BODY.toLowerCase()
    ? trackByCode(name)
    : undefined;
